import { Record } from '@gonfalon/immutable-shim/immutableShim';
import { isNotEmpty, validateRecord } from '@gonfalon/legacy-form-validation/validationUtils';
import type { RoleName } from '@gonfalon/permissions/types';
import { isEmpty } from 'lodash';

import { isAbsoluteURL, urlWithRelayState } from '#utils/urlUtils';

export class SamlAppDetails extends Record({
  ssoAcsUrl: '',
  ssoEntityId: '',
  ssoStartUrl: '',
}) {}

type SamlConfigType = {
  _idpSynced: boolean;
  _testRedirectUrl: string;
  enabled: boolean;
  ssoUrl: string;
  x509Certificate: string;
  requireSso: boolean;
  logoutUrl: string | null;
  defaultRole?: RoleName;
  defaultCustomRoleId?: string;
  isManagingTeams: boolean;
  syncedTeamCount: number;
  unSyncedTeamCount: number;
  shouldDecryptAssertions?: boolean;
  shouldSignRequests?: boolean;
  x509KeystoreId?: string;
};

export class SamlConfig extends Record<SamlConfigType>({
  _idpSynced: false,
  _testRedirectUrl: '',
  enabled: false,
  ssoUrl: '',
  x509Certificate: '',
  requireSso: false,
  logoutUrl: null,
  defaultRole: undefined,
  defaultCustomRoleId: undefined,
  isManagingTeams: false,
  syncedTeamCount: 0,
  unSyncedTeamCount: 0,
  shouldDecryptAssertions: undefined,
  shouldSignRequests: undefined,
  x509KeystoreId: undefined,
}) {
  validate() {
    return validateRecord(this, isNotEmpty('ssoUrl'), isAbsoluteURL('ssoUrl'), isNotEmpty('x509Certificate'));
  }

  isDisabled() {
    return !this.enabled;
  }

  isEnabledForTesting() {
    return this.enabled && !this.requireSso;
  }

  isEnabled() {
    return this.enabled && this.requireSso;
  }

  isEmpty() {
    return isEmpty(this.ssoUrl) && isEmpty(this.x509Certificate);
  }

  disable() {
    return this.merge({
      enabled: false,
      requireSso: false,
    });
  }

  enableForTesting() {
    return this.merge({
      enabled: true,
      requireSso: false,
    });
  }

  enable() {
    return this.merge({
      enabled: true,
      requireSso: true,
    });
  }

  getRedirectUrlWithRelayState(redirectUrl: string) {
    return urlWithRelayState(this._testRedirectUrl, redirectUrl);
  }

  toRep() {
    return {
      ...this.toJS(),
      // When setting a custom role in the BE, we set the role type to undefined.
      defaultRole: !!this.defaultCustomRoleId ? undefined : this.defaultRole,
    };
  }
}

export function createSamlConfig(props = {}) {
  return props instanceof SamlConfig ? props : new SamlConfig(props);
}
